import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import React, { RefObject, useCallback, useContext, useMemo, useState, forwardRef, useImperativeHandle, useRef } from 'react'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CruiseLineMobileInput from './CruiseLineSelectMobile'
import CruiseLineSelectDesktop, { CruiseLineSelectDesktopRef } from './CruiseLineSelectDesktop'
import * as Analytics from 'analytics/analytics'

export interface CruiseLineSelectRef {
  focus: () => void
}

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  dropdownAnchorRef: RefObject<HTMLElement | null>
  drawerMode?: boolean
}

const CruiseLineSelect = forwardRef<CruiseLineSelectRef, Props>((props, ref) => {
  const { onChange, drawerMode, dropdownAnchorRef } = props
  const desktopInputRef = useRef<CruiseLineSelectDesktopRef>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      desktopInputRef.current?.focus()
    },
  }), [])
  const [searchTerm, setSearchTerm] = useState('')
  const { cruiseLines: searchCruiseLines = [] } = useContext(GlobalSearchStateContext)
  const [cruiseLines] = useCruiseSearchFacets({ facetTypes: ['cruise_lines'] })

  const cruiseLineNames = useMemo(() => {
    return cruiseLines
      .filter(({ value }) => searchCruiseLines.includes(value as string))
      .map(({ name }) => name)
  }, [searchCruiseLines, cruiseLines])

  const handlerSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    Analytics.trackClientEvent({
      subject: `Filter input - Cruise Lines - ${e.target.value}`,
      action: 'input',
      category: 'cruise-search-filters',
      type: 'interaction',
    })
    setSearchTerm(e.target.value)
  }, [])

  const handlerClearSearchTerm = useCallback(() => {
    setSearchTerm('')
  }, [])

  return (
    <div>
      <CSSBreakpoint max="mobile">
        <CruiseLineMobileInput
          onChange={onChange}
          cruiseLineNames={cruiseLineNames}
          handlerSearchTerm={handlerSearchTerm}
          handlerClearSearchTerm={handlerClearSearchTerm}
          searchTerm={searchTerm}
        />
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        {drawerMode && <CruiseLineMobileInput
          onChange={onChange}
          cruiseLineNames={cruiseLineNames}
          drawerMode={drawerMode}
          handlerSearchTerm={handlerSearchTerm}
          handlerClearSearchTerm={handlerClearSearchTerm}
          searchTerm={searchTerm}
        />}

        {!drawerMode && <CruiseLineSelectDesktop
          ref={desktopInputRef}
          onChange={onChange}
          cruiseLineNames={cruiseLineNames}
          dropdownAnchorRef={dropdownAnchorRef}
          handlerSearchTerm={handlerSearchTerm}
          handlerClearSearchTerm={handlerClearSearchTerm}
          searchTerm={searchTerm}
        />}
      </CSSBreakpoint>
    </div>
  )
})

CruiseLineSelect.displayName = 'CruiseLineSelect'

export default CruiseLineSelect
